import { motion } from "framer-motion";
import { Star, Shield, Clock, MapPin, Phone, Sparkles, Gift } from "lucide-react";
import { img, IMG_W, IMG_H } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import EmbeddedForm from "@/components/EmbeddedForm";
import { SITE } from "@/lib/site";

const badges = [
  { icon: Shield, label: "Vetted Crews" },
  { icon: Star, label: "5-Star Service" },
  { icon: Clock, label: "Flexible Scheduling" },
  { icon: MapPin, label: "Local & Trusted" },
];

const stats = [
  { value: "15", label: "Cleaning Services", suffix: "+" },
  { value: "13", label: "Areas Served", suffix: "" },
  { value: "100", label: "Satisfaction Focus", suffix: "%" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden hero-cinematic">
      <img
        src={img.heroHome}
        alt="Professional cleaning supplies ready for a Good Choice Cleaning service"
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        width={IMG_W}
        height={IMG_H}
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />

      <div className="relative z-10 container-max px-4 py-32 md:py-36 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            {/* Transform only — no opacity. Chrome excludes anything with
                opacity 0 from being an LCP candidate, and that applies through
                ancestors, so fading this wrapper in meant the <h1> could not
                register as LCP until the bundle had downloaded, parsed and run
                the animation. Lighthouse measured 4,770ms of "element render
                delay" against it. A slide reads the same to a visitor. */}
            <motion.div
              initial={{ y: 30 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-primary/15 backdrop-blur-sm border border-primary/30 text-white text-sm font-semibold px-5 py-2 rounded-full mb-8"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                Trusted Cleaning · Harrisonburg & the Shenandoah Valley
              </motion.div>

              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-[0.98] mb-6 tracking-tight font-bold drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]">
                The Good Choice
                <br />
                for a <span className="text-gradient-primary">Spotless</span>
                <br />
                Space
              </h1>
              <p className="text-white/85 text-lg md:text-xl mb-9 leading-relaxed max-w-xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                Professional residential and commercial cleaning you can count on — trained, vetted crews,
                honest pricing, and a fresh, healthy space every time.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]"
                >
                  Get Free Estimate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl border-2 border-white/25 text-white bg-white/5 hover:bg-white/15 hover:text-white hover:border-white/40 backdrop-blur-sm"
                >
                  <a href={SITE.phoneHref}><Phone className="h-5 w-5" /> {SITE.phoneDisplay}</a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <Gift className="h-6 w-6 text-secondary-bright shrink-0" />
                <span className="text-white/90 font-semibold text-sm">
                  Refer &amp; earn — <span className="text-secondary-bright font-bold">a free regular cleaning</span> for every 2 referrals!
                </span>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl pointer-events-none -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.35), transparent 70%)" }}
            />
            <div className="relative rounded-2xl overflow-hidden shadow-premium-lg bg-card p-5 sm:p-6 border border-border/60">
              <h2 className="font-heading text-xl text-foreground mb-1 font-bold">Request Your Free Estimate</h2>
              <p className="text-muted-foreground text-sm mb-4">Tell us about your space — we'll get right back to you.</p>
              <EmbeddedForm />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-16 md:mt-20 glass-card rounded-2xl p-6 md:p-8 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-secondary-bright">{stat.suffix}</span>
                </div>
                <div className="text-white/75 text-xs font-semibold uppercase tracking-wider mt-1">{stat.label}</div>
              </motion.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-white/80 text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-16 bg-background leading-none z-10">
        <svg viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="block w-full h-full" preserveAspectRatio="none">
          <path d="M0 32C240 64 480 64 720 32C960 0 1200 0 1440 32V64H0V32Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
